import { Box } from '@mui/material';
import React, { ReactElement, useState } from 'react';
import { Event, useReceiptsApi } from 'services/api';
import { Button } from 'components/ui';
import { QrCodeIcon } from 'components/icons';
import { colorWhite, colorSkeletonBackground } from 'customColors';
import { EventCheckInSheet } from 'components/modals/event-check-in';
import { useSnackbar } from 'notistack';
import { EventBrief, EventBriefProps } from './EventBrief';

export interface ReservedEventBriefProps extends EventBriefProps {
  status: Event['status'];
  receiptId: string;
  checkedIn: boolean;
  event: Event;
}

export const ReservedEventBrief = ({
  status,
  receiptId,
  checkedIn,
  event,
  ...props
}: ReservedEventBriefProps): ReactElement => {
  const receiptsApi = useReceiptsApi();
  const { enqueueSnackbar } = useSnackbar();
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [tickets, setTickets] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckIn = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const result = await receiptsApi.getReceipt(receiptId);
      if (!result.success) {
        enqueueSnackbar('Failed to load ticket', { variant: 'error' });
        return;
      }

      setTickets(result.value.tickets);
      setShowTicketModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      maxWidth={{ xs: '100%', sm: '100%', md: '100%', lg: '100%' }}
      sx={{
        backgroundColor: colorWhite,
        textAlign: 'left',
        border: `1px solid ${colorSkeletonBackground}`,
        lineHeight: 0,
        boxShadow: 0,
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: '1px 1px 15px rgba(0,0,0,.2)',
        },
      }}
    >
      <EventBrief
        {...props}
        disableLinks={false}
        hideBorder={true}
        hideLogos={true}
      />
      <Box p={11} pt={16}>
        <Button
          variant="vibrant"
          minHeight={50}
          sx={{
            width: '100%',
          }}
          onClick={handleCheckIn}
          disabled={isLoading}
        >
          <Box display="flex" alignItems="center" gap={8}>
            <QrCodeIcon sx={{ width: 24, height: 24 }} />
            {isLoading ? 'Loading…' : 'Check In'}
          </Box>
        </Button>
      </Box>

      <EventCheckInSheet
        open={showTicketModal}
        onClose={() => setShowTicketModal(false)}
        receiptId={receiptId}
        tickets={tickets.map(t => ({
          firstName: t.firstName,
          lastName: t.lastName,
          email: t.email,
          ticketNumber: t.ticketNumber,
          typeId: t.typeId,
          checkedInDate: t.checkInDate,
        }))}
        event={event}
      />
    </Box>
  );
};
